import React, { useEffect } from 'react'
import classNames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { Form, Field, useWatch, useForm, isEmpty } from '../../../../form'
import MenuItem from '@material-ui/core/MenuItem'
import { Translation, useTranslation } from 'react-i18next'
import { withStyles } from '@material-ui/core/styles'
import {
  getInvoiceUserEmail,
  getUserCompanyUpdateInfo
} from '../../../../actions'
import { invoiceDeliveryAndDetailsValidate } from '../../../../containers/validate'
import Button from '@material-ui/core/Button'
import mobileView from '../../../../utils/mobile.utils'

const styles = (theme) => ({
  select: {
    fontSize: '14px',
    marginTop: theme.spacing(4),
    cursor: 'pointer',
    border: '1px solid gray'
  },
  textField: {
    marginTop: theme.spacing(4),
    border: '1px solid gray'
  },
  resize: {
    fontSize: '14px'
  },
  checkbox: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#808080',
    marginBottom: '10px'
  },
  button: {
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '15px',
    borderRadius: '10px'
  },
  buttonBack: {
    padding: !mobileView ? '2px 30px' : '4px 20px',
    color: 'rgb(83 77 77)',
    backgroundColor: '#fff',
    border: '2px solid #44C0CC',
    borderRadius: mobileView ? '4px' : '',
    [theme.breakpoints.down('375')]: {
      padding: '1px 12px'
    },
    '&:hover': {
      backgroundColor: '#E0F7FA'
    }
  },
  buttonNext: {
    padding: !mobileView ? '0 25px' : '2px 20px',
    color: '#fff',
    backgroundColor: '#44C0CC',
    borderRadius: mobileView ? '4px' : '',
    [theme.breakpoints.down('375')]: {
      padding: '1px 12px'
    },
    '&:hover': {
      backgroundColor: '#3BA9B5'
    }
  }
})

const InvoiceDetailsForm = ({ classes, goBack, goNext, formValues }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const form = useForm({
    name: 'invoice',
    onSubmit: onSubmit,
    defaultValues: formValues,
    validator: (values) => invoiceDeliveryAndDetailsValidate(values),
    keepEmptyValuesOnSubmit: false
  })

  useEffect(() => {
    form.reset(formValues)
  }, [formValues])

  const {
    control,
    formState: { errors }
  } = form

  const [attemptingSubmit, setAttemptingSubmit] = React.useState(false)

  function onSubmit(values) {
    goNext(values)
  }

  function setValue(name, value) {
    form?.setValue(name, value)
  }

  React.useEffect(() => {
    if (attemptingSubmit && !isEmpty(errors)) {
      setAttemptingSubmit(false)
    }
  }, [attemptingSubmit, errors])

  React.useEffect(() => {
    dispatch(getInvoiceUserEmail(0))
    dispatch(getUserCompanyUpdateInfo())
  }, [])

  return (
    <Form
      {...form}
      style={{
        display: 'flex',
        height: '100%',
        flexDirection: 'column'
      }}
    >
      <div
        className="row"
        style={{
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: mobileView ? '0 20px' : ''
        }}
      >
        <div className="col-xs-12 col-sm-4 col-lg-4">
          <InvoiceDetails
            classes={classes}
            setValue={setValue}
            control={control}
          />
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          gap: '15px',
          justifyContent: mobileView ? 'center' : 'flex-end',
          margin: '40px 10px 15px'
        }}
      >
        <Button
          className={classNames(classes.buttonBack, classes.button)}
          onClick={() => goBack(form.getValues())}
        >
          {t('asiakkaat.back')}
        </Button>
        <Button
          className={classNames(classes.buttonNext, classes.button)}
          type="submit"
          onClick={() => setAttemptingSubmit(true)}
        >
          {t('asiakkaat.next')}
          <span
            style={{
              display: 'inline-block',
              transform: 'scaleX(1.5)',
              fontSize: '18px',
              letterSpacing: '-2px'
            }}
          >
            &nbsp; &gt;
          </span>
        </Button>
      </div>
    </Form>
  )
}

export const InvoiceDetails = ({ classes, setValue, control }) => {
  const isEstonian = useSelector((state) => state.language?.lang) === 'est'

  return (
    <Translation>
      {(t) => {
        return (
          <div className="panel panel-default">
            <h3
              style={{
                textAlign: !mobileView ? 'left' : 'center',
                fontSize: mobileView && '20px',
                fontWeight: '600',
                color: '#000',
                textTransform: 'none'
              }}
            >
              {t('invoice.laskunh3')}
            </h3>
            <div>
              <div>
                <div>
                  <Field
                    name="billing_date"
                    label={t('invoice.labela1')}
                    className={classNames(classes.textField, classes.resize)}
                    inputType="datepicker"
                    control={control}
                    minDate={new Date().setMonth(new Date().getMonth() - 1)}
                    // KAS-340 Date of invoicing is limited in 1 month back
                  />
                </div>
                <div>
                  <Field
                    name="overdue"
                    inputType="select"
                    label={t('invoice.labela2')}
                    className={classes.select}
                    control={control}
                  >
                    {_createOverdueItems(t)}
                  </Field>
                </div>
              </div>
              <div>
                <DueDateField
                  t={t}
                  classes={classes}
                  setValue={setValue}
                  control={control}
                />
              </div>
              <div>
                <Field
                  name="invoice_reference"
                  inputType="textfield"
                  label={t('invoice.labela4')}
                  className={classNames(classes.textField, classes.resize)}
                  control={control}
                  labelStyle={
                    mobileView && isEstonian ? { marginTop: '15px' } : {}
                  }
                />
              </div>
              <div>
                <Field
                  name="description"
                  inputType="textfield"
                  label={t('invoice.labela5')}
                  className={classNames(classes.textField, classes.resize)}
                  control={control}
                />
              </div>
            </div>
          </div>
        )
      }}
    </Translation>
  )
}

const DueDateField = ({ t, classes, setValue, control }) => {
  const watch = useWatch({
    fields: ['overdue', 'billing_date'],
    control
  })
  const dueDate = new Date(watch.billing_date.valueOf())
  dueDate.setDate(watch.billing_date.getDate() + watch.overdue)

  return (
    <Field
      name="due_date"
      label={t('invoice.labela3')}
      className={classNames(classes.textField, classes.resize)}
      inputType="datepicker"
      setValue={() => setValue('due_date', dueDate)}
      watchValue={dueDate}
      disabled={true}
      control={control}
    />
  )
}

const _createOverdueItems = (t) => {
  let overdueItems = []
  for (let i = 0; i <= 60; i++) {
    overdueItems.push(
      <MenuItem key={i} value={i}>
        {`${i} ${t('invoice.days')}`}
      </MenuItem>
    )
  }
  return overdueItems
}

export default withStyles(styles)(InvoiceDetailsForm)
